// components/DetailPanel.jsx
import { fmt, pct } from "../mockData";
import { StatusBadge, SourceBadge, ConfidenceBadge, FallbackBadge, PlanBadge } from "./Badges";

export default function DetailPanel({ rec, onClose, onApprove, onReject }) {
  if (!rec) return null;
  const uplift = pct(rec.base_rate_single, rec.recommended_price);

  return (
    <div style={{
      width: 310, flexShrink: 0,
      background: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: 14, padding: "24px 22px",
      position: "sticky", top: 20,
      boxShadow: "0 8px 32px rgba(0,0,0,0.10)",
      fontFamily: "'Outfit', sans-serif",
      maxHeight: "calc(100vh - 100px)",
      overflowY: "auto",
    }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
        <div>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, color: "#0f172a", fontWeight: 700 }}>
            {rec.type_name} Room
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 3, fontWeight: 500 }}>
            {new Date(rec.checkin_date + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
          </div>
        </div>
        <button
          onClick={onClose}
          style={{ background: "#f1f5f9", border: "none", borderRadius: 8, width: 30, height: 30, cursor: "pointer", color: "#64748b", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}
        >×</button>
      </div>

      {/* Badges */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 16 }}>
        <StatusBadge status={rec.status} />
        <SourceBadge source={rec.source_channel} />
        {rec.plan_code && <PlanBadge code={rec.plan_code} />}
        {rec.rate_fallback && <FallbackBadge />}
      </div>

      {/* Price breakdown */}
      <div style={{ marginBottom: 16 }}>
        {[
          { label: "Base Rate",      value: fmt(rec.base_rate_single),     accent: "#94a3b8",  bold: false },
          { label: "AI Recommended", value: fmt(rec.recommended_price),    accent: "#d97706",  bold: true, highlight: true },
          { label: "Competitor Avg", value: fmt(rec.competitor_avg_price), accent: "#6d28d9",  bold: false },
          ...(rec.final_price ? [{ label: "Final Price", value: fmt(rec.final_price), accent: "#15803d", bold: true }] : []),
        ].map(item => (
          <div key={item.label} style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "10px 14px", marginBottom: 6,
            background: item.highlight ? "#fffbeb" : "#f8fafc",
            borderLeft: `3px solid ${item.accent}`,
            borderRadius: "0 8px 8px 0",
          }}>
            <span style={{ fontSize: 12, color: "#64748b", fontWeight: 500 }}>{item.label}</span>
            <span style={{
              fontFamily: "'DM Mono',monospace",
              fontSize: item.bold ? 15 : 13,
              fontWeight: item.bold ? 700 : 500,
              color: item.bold ? "#0f172a" : "#475569",
            }}>{item.value}</span>
          </div>
        ))}
      </div>

      {/* Uplift + Confidence */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
        <div style={{ background: "#f0fdf4", borderRadius: 10, padding: "12px", textAlign: "center" }}>
          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 20, fontWeight: 800, color: "#15803d" }}>
            +{uplift}%
          </div>
          <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.8, marginTop: 3, fontWeight: 600 }}>
            Uplift vs Base
          </div>
        </div>
        <div style={{ background: "#f8fafc", borderRadius: 10, padding: "12px", textAlign: "center" }}>
          <ConfidenceBadge score={rec.confidence_score} />
          <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.8, marginTop: 6, fontWeight: 600 }}>
            AI Confidence
          </div>
        </div>
      </div>

      {/* Occupancy */}
      <div style={{ background: "#f8fafc", borderRadius: 10, padding: "12px 14px", marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
          <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>Occupancy on this date</span>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 13, fontWeight: 700, color: rec.occupancy_rate >= 80 ? "#15803d" : rec.occupancy_rate >= 60 ? "#b45309" : "#b91c1c" }}>
            {rec.occupancy_rate}%
          </span>
        </div>
        <div style={{ background: "#e2e8f0", borderRadius: 4, height: 6 }}>
          <div style={{
            width: `${rec.occupancy_rate}%`, height: "100%", borderRadius: 4,
            background: rec.occupancy_rate >= 80 ? "#16a34a" : rec.occupancy_rate >= 60 ? "#d97706" : "#ef4444",
            transition: "width 0.5s ease",
          }} />
        </div>
      </div>

      {/* AI Reasoning */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, fontWeight: 700 }}>
          AI Reasoning
        </div>
        <p style={{
          fontSize: 12, color: "#475569", lineHeight: 1.75,
          margin: 0, background: "#fffbeb",
          borderRadius: 8, padding: "12px 14px",
          borderLeft: "3px solid #d97706",
        }}>
          {rec.reasoning}
        </p>
      </div>

      {/* Factors */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, fontWeight: 700 }}>
          Key Factors
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {rec.factors.map(f => (
            <span key={f} style={{
              fontSize: 11, background: "#f1f5f9", color: "#334155",
              border: "1px solid #e2e8f0", borderRadius: 5, padding: "4px 9px",
              fontWeight: 600,
            }}>{f}</span>
          ))}
          {rec.is_holiday && (
            <span style={{
              fontSize: 11, background: "#fffbeb", color: "#b45309",
              border: "1px solid #fde68a", borderRadius: 5, padding: "4px 9px",
              fontWeight: 600,
            }}>🎉 {rec.holiday_name}</span>
          )}
        </div>
      </div>

      {/* Context grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 18 }}>
        {[
          { label: "Check-in",  value: rec.checkin_date          },
          { label: "Channel",   value: rec.source_channel         },
          { label: "Holiday",   value: rec.is_holiday ? rec.holiday_name : "None" },
          { label: "Events",    value: rec.local_events && rec.local_events !== "No major events" ? "Yes" : "None" },
        ].map(item => (
          <div key={item.label} style={{ background: "#f8fafc", borderRadius: 8, padding: "9px 12px" }}>
            <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 600 }}>{item.label}</div>
            <div style={{ fontSize: 12, color: "#1e293b", marginTop: 3, fontWeight: 600 }}>{item.value}</div>
          </div>
        ))}
      </div>

      {/* Actions */}
      {rec.status === "PENDING" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <button
            className="dp-btn dp-btn-success"
            style={{ justifyContent: "center", padding: "12px" }}
            onClick={() => onApprove(rec.id)}
          >
            ✓ Approve {fmt(rec.recommended_price)}
          </button>
          <button
            className="dp-btn dp-btn-danger"
            style={{ justifyContent: "center", padding: "10px" }}
            onClick={() => onReject(rec.id)}
          >
            ✕ Reject Recommendation
          </button>
        </div>
      )}
    </div>
  );
}
